import React, { useState, useContext } from "react";
import Button from "@ui-components/Button";
import { PencilIcon, TrashIcon } from "@heroicons/react/20/solid";
import { PriceListOutputProps } from "@feature/price-list/types";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { NotificationsContext } from "ui-components/Notifications";
import Modal from "@ui-components/Modal";

export const PriceListOutput = ({
  form,
  result,
  id,
  setPriceListData,
  setStartSearch,
  fetchHistory,
}: PriceListOutputProps) => {

  // context
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();

  // state
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // utils
  const formatPrice = (p: number) => Intl.NumberFormat(intl.locale, {style: "currency", currency: "EUR"}).format(p);
  const onDelete = async () => {
    try {
      setLoading(true);
      await countryApiRequest!.delete(`/remote_calculation/${id}`);
      push({
        type: "success",
        title: intl.formatMessage({id: "price_list_deleted", defaultMessage: "Listino eliminato"}),
      });
      fetchHistory();
    } finally {
      setLoading(false);
    }
  }
  const hasTintometerPrice = ![null, undefined].includes(result.price.tintometer);

  const data = [
    {
      label: {id: "color", defaultMessage: "Colore"},
      value: (
        <div className="flex flex-row font-medium">
          <div
            className="w-5 h-5 mr-2 border-black"
            style={{
              backgroundColor: `rgb(${form.color.rgb.join(", ")})`,
              borderWidth: "1px",
            }}
          ></div>
          {form.color.code}
        </div>
      )
    },
    {label: {id: "product_family", defaultMessage: "Famiglia prodotto"}, value: form.kColorProduct.des_family},
    {label: {id: "product", defaultMessage: "Prodotto"}, value: form.kColorProduct.des_prod_comm},
    {label: {id: "package", defaultMessage: "Formato"}, value: `${form.productPackage} ${form.kColorProduct.base_um}`},
    {
      label: {id: "public_price", defaultMessage: "Prezzo di Listino"},
      value: formatPrice(result.price.kerakoll),
    },
    ...(hasTintometerPrice
      ? [{label: {id: "price_tintometer", defaultMessage: "Prezzo tintometro"}, value: formatPrice(result.price.tintometer)}]
      : []
    ),
  ]

  return <>
    <div className="bg-ivory px-5 py-4 flex flex-col gap-4">
      <p className="text-base font-semibold">
        <FormattedMessage id="parameters" defaultMessage="Parametri" />
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {data.map(({label, value}, index) => (
          <React.Fragment key={index}>
            <div className={label.id.startsWith("price") ? "font-semibold" : ""}>
              <FormattedMessage id={label.id} defaultMessage={label.defaultMessage} />
            </div>
            <div className="font-medium">
              {value}
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button
          full
          // @ts-ignore
          Icon={PencilIcon}
          submitting={loading}
          onClick={() => {
            let _priceListData = { ...form, edit_id: id };
            setPriceListData(_priceListData);
            setStartSearch(true);
          }}
        >
          <FormattedMessage id="edit" defaultMessage="Modifica" />
        </Button>
        <Button
          full
          styleType="white"
          submitting={loading}
          // @ts-ignore
          Icon={TrashIcon}
          onClick={async () => {
            try {
              setLoading(true);
              await countryApiRequest!.delete(`/remote_calculation/${id}`);
              push({
                type: "success",
                title: intl.formatMessage({
                  id: "price_list_deleted",
                  defaultMessage: "Listino eliminato",
                }),
              });
              setLoading(false);
              fetchHistory();
            } catch (e) {
              console.error(e);
            }
          }}
        >
          <FormattedMessage id="delete" defaultMessage="Elimina" />
        </Button>
      </div>
    </div>
    {openDeleteDialog && (
      <Modal
        title={intl.formatMessage({id: "confirm_delete", defaultMessage: "Conferma eliminazione"})}
        description={intl.formatMessage({id: "confirm_delete_description", defaultMessage: "Sei sicuro di voler eliminare questo listino? Questa operazione non è reversibile."})}
        children={undefined}
        onConfirm={onDelete}
        onDismiss={() => setOpenDeleteDialog(false)}
        onExit={() => setOpenDeleteDialog(false)}
      />
    )}
  </>
};
